import { useState } from "react"
import { Form } from "antd"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { Collapse } from "components/ui/collapse/Collapse"
import { Select } from "components/ui/select/Select"
import { TextArea } from "components/ui/inputs/TextArea"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { useChatStore, useBackendChatHistoryStore, useBidRequestFormData } from "store/aiAgent.store"
import { useAddBidRequest, useSendFeedback } from "hooks/api/aiAgent/aiAgent.hook"
import { useSearchParams } from "react-router-dom"
import type { TAvailableFields, TGroupedAvailableFields, extractedFieldsItem } from "types/aiAgent.type"
import style from "./BidRequestForm.module.scss"

const {
	bidRequestForm,
	text,
	collapse,
	formItem,
	select,
	collapseLabel,
	itemName,
	collapseChildren,
	submitButton,
	feedbackTextArea,
	feedbackToggle,
} = style

type Props = {
	bidRequestData: {
		extractedFields: extractedFieldsItem | null
		availableFields: TGroupedAvailableFields | null
	}
	langsmithTraceId: string | undefined
	selectedSeries: string
	answerIndex: number
}

export const BidRequestForm = ({ bidRequestData, langsmithTraceId, selectedSeries, answerIndex }: Props) => {
	const [changedFields, setChangedFields] = useState<Record<string, boolean>>({})
	const [fieldFeedback, setFieldFeedback] = useState<Record<string, string>>({})
	const [visibleFeedback, setVisibleFeedback] = useState<string | null>(null)

	const { mutate: addBidRequestMutation, isPending: addBidRequestPending } = useAddBidRequest()
	const { mutate: sendFeedbackMutation } = useSendFeedback()
	const { addQuestion, addAnswer, setFailedAnswer } = useChatStore()
	const { addBackendChatMessage } = useBackendChatHistoryStore()
	const { isBidRequestSubmitted, setSubmittedBidRequest } = useBidRequestFormData()
	const [searchParams] = useSearchParams()
	const drawingId = Number(searchParams.get("drawingID"))
	const [form] = Form.useForm()

	const formSubmitted = isBidRequestSubmitted(answerIndex)

	// Handle field value changes to track if field was modified
	const handleFieldChange = (fieldKey: string, newValue: string | number) => {
		const initialValues = getInitialFormValues()
		const initialValue = initialValues[fieldKey]
		const valueAsString = String(newValue)

		// Mark field as changed if new value differs from initial
		if (valueAsString !== initialValue) {
			setChangedFields((prev) => ({ ...prev, [fieldKey]: true }))
			// Auto-show feedback textarea when field is changed
			setVisibleFeedback(fieldKey)
		} else {
			// Remove from changed fields if value is back to original
			setChangedFields((prev) => {
				const updated = { ...prev }
				delete updated[fieldKey]
				return updated
			})
			// Clear feedback if field is reverted
			setFieldFeedback((prev) => {
				const updated = { ...prev }
				delete updated[fieldKey]
				return updated
			})
			// Hide feedback textarea
			if (visibleFeedback === fieldKey) {
				setVisibleFeedback(null)
			}
		}
	}

	// Handle feedback text change for a field
	const handleFeedbackChange = (fieldKey: string, feedbackText: string) => {
		setFieldFeedback((prev) => ({ ...prev, [fieldKey]: feedbackText }))
	}

	// Toggle feedback visibility
	const toggleFeedbackVisibility = (fieldKey: string) => {
		setVisibleFeedback((prev) => (prev === fieldKey ? null : fieldKey))
	}

	// Transform extractedFields to get initial values for form initialization
	const getInitialFormValues = () => {
		const extractedFieldsData = bidRequestData.extractedFields
		const initialValues: Record<string, string> = {}

		if (extractedFieldsData) {
			// Use extracted field values as initial values
			for (const [fieldKey, extractedValue] of Object.entries(extractedFieldsData)) {
				initialValues[fieldKey] = String(extractedValue)
			}
		}
		return initialValues
	}

	// Detect field changes between current form values and initial form values
	const detectFieldChanges = (currentFormValues: Record<string, string>, initialFormValues: Record<string, string>) => {
		const incorrectFields: string[] = []
		const fieldCorrections: Record<string, { original: string; corrected: string }> = {}

		for (const [fieldKey, initialValue] of Object.entries(initialFormValues)) {
			const formValue = currentFormValues[fieldKey]
			if (formValue && formValue !== initialValue) {
				incorrectFields.push(fieldKey)
				fieldCorrections[fieldKey] = {
					original: initialValue,
					corrected: formValue,
				}
			}
		}

		return {
			hasChanges: incorrectFields.length > 0,
			incorrectFields,
			fieldCorrections,
		}
	}

	// Send feedback based on detected changes
	const sendBidRequestFeedback = (
		hasChanges: boolean,
		incorrectFields: string[],
		fieldCorrections: Record<string, { original: string; corrected: string }>,
	) => {
		if (!langsmithTraceId) {
			return
		}

		const drawingUrl = window.location.href

		if (!hasChanges) {
			// All fields are correct - send positive feedback
			sendFeedbackMutation({
				drawingId,
				langsmithTraceId,
				type: "POSITIVE",
				drawingUrl,
			})
		} else {
			// Some fields are incorrect - send negative feedback
			const incorrectFieldsList = incorrectFields.map((field) => `* ${field}`).join("\n")
			const correctionsList = Object.entries(fieldCorrections)
				.map(([fieldKey, { original, corrected }]) => `* ${fieldKey}: "${original}" → "${corrected}"`)
				.join("\n")

			// Collect user feedback explanations for changed fields
			const userExplanationsList = incorrectFields
				.map((fieldKey) => {
					const explanation = fieldFeedback[fieldKey]
					return explanation ? `* ${fieldKey}: ${explanation}` : null
				})
				.filter(Boolean)
				.join("\n")

			// Build feedback text with corrections and user explanations
			let feedbackText = `The LLM selected incorrect values for the following fields:\n${incorrectFieldsList}\n\nUser corrections:\n${correctionsList}`
			if (userExplanationsList) {
				feedbackText += `\n\nUser explanations:\n${userExplanationsList}`
			}

			sendFeedbackMutation({
				drawingId,
				langsmithTraceId,
				type: "NEGATIVE",
				feedback: feedbackText,
				drawingUrl,
				manuallySubmittedByUser: false,
			})
		}
	}

	// Create field correction conversation messages
	const createFieldCorrectionMessages = (
		incorrectFields: string[],
		fieldCorrections: Record<string, { original: string; corrected: string }>,
	) => {
		// Format available options for incorrect fields
		const availableOptionsMarkdown = incorrectFields
			.map((fieldKey) => {
				// Find the field in availableFields structure
				if (bidRequestData.availableFields) {
					for (const [, groupFields] of Object.entries(bidRequestData.availableFields)) {
						if (typeof groupFields === "object" && !Array.isArray(groupFields) && groupFields !== null) {
							if (fieldKey in groupFields) {
								const options = groupFields[fieldKey] as TAvailableFields
								const optionsList = options.map((option) => `- ${option.text}`).join("\n")
								return `**${fieldKey}:**\n${optionsList}`
							}
						}
					}
				}
				return `**${fieldKey}:** [options not found]`
			})
			.join("\n\n")

		// Format corrections in markdown
		const correctionsMarkdown = Object.entries(fieldCorrections)
			.map(([fieldKey, { corrected }]) => `**${fieldKey}:** "${corrected}"`)
			.join("\n")

		// Format incorrectly selected fields in markdown
		const incorrectFieldsMarkdown = incorrectFields.map((field) => `**${field}**`).join(", ")

		return [
			{ role: "user", content: `the fields ${incorrectFieldsMarkdown} were selected incorrectly.` },
			{
				role: "assistant",
				content: `please specify the correct values for the fields. Here are the possible values for the fields you mentioned as incorrectly selected: ${availableOptionsMarkdown}`,
			},
			{ role: "user", content: `here are the correct values for the fields: ${correctionsMarkdown}` },
		]
	}

	const updateBackendChatHistory = (
		hasChanges: boolean,
		incorrectFields: string[],
		fieldCorrections: Record<string, { original: string; corrected: string }>,
	) => {
		if (!hasChanges) {
			// No fields were changed
			addBackendChatMessage({ role: "user", content: "please submit the bid request to Stevens." })
		} else {
			// Fields were changed - add correction conversation
			const correctionMessages = createFieldCorrectionMessages(incorrectFields, fieldCorrections)
			correctionMessages.forEach((message) => {
				addBackendChatMessage(message)
			})
			addBackendChatMessage({ role: "user", content: "please submit the bid request to Stevens." })
		}
	}

	const bidRequestSubmitHandle = () => {
		// Get current form values and initial form values
		const currentFormValues = form.getFieldsValue()
		const initialFormValues = getInitialFormValues()

		// Detect changes by comparing current values with initial values
		const { hasChanges, incorrectFields, fieldCorrections } = detectFieldChanges(currentFormValues, initialFormValues)

		// Update backend chat history
		updateBackendChatHistory(hasChanges, incorrectFields, fieldCorrections)

		// Send feedback based on detected changes
		sendBidRequestFeedback(hasChanges, incorrectFields, fieldCorrections)

		// Transform extractedFields to get just the text values for API
		const extractedFieldsData = bidRequestData.extractedFields
		const transformedLlmSelections: Record<string, string> = {}
		if (extractedFieldsData) {
			for (const [key, value] of Object.entries(extractedFieldsData)) {
				transformedLlmSelections[key] = String(value)
			}
		}

		addBidRequestMutation(
			{
				langsmithTraceId,
				drawingId,
				llmSelections: transformedLlmSelections,
				userConfirmation: currentFormValues,
				seriesName: selectedSeries,
			},
			{
				onSuccess: () => {
					addQuestion("Confirm and submit Bid Request", "system")
					addAnswer("Bid Request confirmed and submitted!")
					setSubmittedBidRequest(answerIndex)
					addBackendChatMessage({ role: "assistant", content: "The Bid Request was submitted to Stevens system" })
				},
				onError: () => {
					setFailedAnswer()
					addBackendChatMessage({
						role: "assistant",
						content: "The submit of the bid request failed. Fix error, send bid request",
					})
				},
			},
		)
	}

	return (
		<div className={bidRequestForm}>
			<span className={text}>Is the selected properties correct?</span>
			<Form initialValues={getInitialFormValues()} form={form}>
				{bidRequestData.availableFields &&
					Object.entries(bidRequestData.availableFields).map(([groupKey, groupFields]) => {
						// Handle nested structure with groups
						if (typeof groupFields === "object" && !Array.isArray(groupFields) && groupFields !== null) {
							return (
								<Collapse
									key={groupKey}
									className={collapse}
									defaultActiveKey={[groupKey]}
									destroyOnHidden={true}
									items={[
										{
											key: groupKey,
											label: <span className={collapseLabel}>{groupKey}</span>,
											"data-testid": "collapse-items",
											children: (
												<div className={collapseChildren}>
													{Object.entries(groupFields).map(([fieldKey, options]) => {
														// Convert options to AntD format - each option has text and optional default
														const typedOptions = options as TAvailableFields
														const selectOptions = typedOptions.map((option) => ({
															label: option.text,
															value: option.text,
														}))

														return (
															<div key={fieldKey}>
																<span className={itemName}>{fieldKey}</span>
																<Form.Item className={formItem} name={fieldKey}>
																	<Select
																		disabled={formSubmitted}
																		variant="outlined"
																		className={select}
																		options={selectOptions}
																		onChange={(value) => handleFieldChange(fieldKey, value)}
																	/>
																</Form.Item>
																{changedFields[fieldKey] && !formSubmitted && (
																	<>
																		{visibleFeedback === fieldKey ? (
																			<div className={feedbackTextArea}>
																				<TextArea
																					placeholder="Share your feedback (optional)"
																					minRows={2}
																					value={fieldFeedback[fieldKey] || ""}
																					onChange={(e) => handleFeedbackChange(fieldKey, e.target.value)}
																					onBlur={() => setVisibleFeedback(null)}
																				/>
																			</div>
																		) : (
																			<span
																				className={feedbackToggle}
																				onClick={() => toggleFeedbackVisibility(fieldKey)}
																			>
																				Show feedback
																			</span>
																		)}
																	</>
																)}
															</div>
														)
													})}
												</div>
											),
										},
									]}
								/>
							)
						}
						return null
					})}
				{addBidRequestPending ? (
					<Skeleton rows={1} />
				) : !formSubmitted ? (
					<ButtonPrimary className={submitButton} onClick={() => bidRequestSubmitHandle()}>
						Confirm and submit Bid Request
					</ButtonPrimary>
				) : null}
			</Form>
		</div>
	)
}
